import Image from 'next/image'
import { cn } from '@/lib/utils'
import { getDisplayImageUrl } from '@/lib/image-upload'

/** Fallback shown for any account without a profile picture. */
export const FALLBACK_AVATAR = '/images/logo-no-back.png'

/**
 * Snap the requested pixel size to a short ladder so the same face rendered
 * at 22, 30 and 36 CSS px shares one cached download instead of three.
 */
const SIZE_LADDER = [64, 96, 128, 192, 256, 512]
function renderSize(cssSize: number): number {
  const wanted = cssSize * 2 // retina
  return SIZE_LADDER.find((s) => s >= wanted) ?? SIZE_LADDER[SIZE_LADDER.length - 1]
}

/**
 * A user's avatar — their profile picture when set, otherwise the Gamerplug
 * logo. Use everywhere a user is shown so missing pictures look consistent.
 */
export function UserAvatar({
  url,
  alt,
  size = 36,
  className,
  style,
}: {
  url?: string | null
  alt: string
  size?: number
  className?: string
  style?: React.CSSProperties
}) {
  const hasPic = !!url
  // Stored avatar URLs point at the original upload (often 200 KB to 1 MB).
  // Ask Storage's render endpoint for a copy sized for the circle instead:
  // 2x the CSS size covers retina, and it decodes legacy HEIC uploads too.
  // Non-Supabase URLs (Steam, Dicebear, local) pass through unchanged.
  const src = hasPic ? (getDisplayImageUrl(url, renderSize(size)) ?? url!) : FALLBACK_AVATAR
  return (
    <Image
      src={src}
      alt={alt}
      width={size}
      height={size}
      unoptimized
      className={cn(
        'shrink-0 rounded-full',
        hasPic ? 'object-cover' : 'bg-secondary object-contain p-1',
        className
      )}
      style={{ width: size, height: size, ...style }}
    />
  )
}
